/**
 * Comprimi un'immagine lato client prima dell'upload:
 * - Ridimensiona a max 2048px sul lato lungo (mantiene proporzioni)
 * - Riconverte in JPEG con qualità 0.85 (default)
 * - PNG/WEBP con trasparenza vengono mantenuti (no perdita alfa)
 *
 * @param {File} file - immagine input
 * @param {object} opts - { maxSide=2048, quality=0.85 }
 * @returns {Promise<{ blob: Blob, base64: string, name: string, mime: string, originalSize: number, compressedSize: number }>}
 */
export async function compressImage(file, opts = {}) {
  const { maxSide = 2048, quality = 0.85 } = opts;

  // PNG/WEBP con possibile alfa: se sotto 1.5MB lascia stare, altrimenti comprimi a JPEG
  const isPngOrWebp = /image\/(png|webp)/i.test(file.type);
  const skipResize = file.size < 600 * 1024;
  if (skipResize) {
    const base64 = await readAsDataURL(file);
    return {
      blob: file,
      base64,
      name: file.name,
      mime: file.type,
      originalSize: file.size,
      compressedSize: file.size,
    };
  }

  const dataUrl = await readAsDataURL(file);
  const img = await loadImage(dataUrl);

  let { width, height } = img;
  if (width > maxSide || height > maxSide) {
    const ratio = Math.min(maxSide / width, maxSide / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // Sfondo bianco se converto da PNG senza alpha al JPEG
  if (!isPngOrWebp) {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(img, 0, 0, width, height);

  const targetMime = isPngOrWebp ? file.type : 'image/jpeg';
  const targetExt = isPngOrWebp ? (file.type === 'image/png' ? 'png' : 'webp') : 'jpg';

  const blob = await new Promise((res) => canvas.toBlob(res, targetMime, quality));
  const base64 = await blobToBase64(blob);

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return {
    blob,
    base64,
    name: `${baseName}.${targetExt}`,
    mime: targetMime,
    originalSize: file.size,
    compressedSize: blob.size,
  };
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * POST JSON con base64 mostrando progresso UPLOAD reale via XHR.
 *
 * @param {string} url
 * @param {object} body - JSON serializzabile
 * @param {object} headers - { Authorization: ... }
 * @param {(percent:number)=>void} onProgress - 0..100
 * @returns {Promise<{ok:boolean, status:number, data:any}>}
 */
export function postJsonWithProgress(url, body, headers = {}, onProgress) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      let data = null;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { data = null; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: null });
    xhr.send(JSON.stringify(body));
  });
}
